import {
  Directive, ElementRef, Input, OnChanges, OnInit, Pipe, PipeTransform, SimpleChanges
} from '@angular/core';
import {Order} from './../../contracts/order';
import {OrderDetailsService} from './../../services/order-details/order-details.service';

interface SubmitButtonTxtInterface {
  buttonType: string;
  drugsAdded?: number;
  drugsRemoved?: number;
  drugsInBox?: any[];
  showReconsentFlow?: boolean;
}

export class SubmitButtonTxt {
  constructor(private _order: OrderDetailsService) {
  }

  /**
   * Generate button text
   */
  getText(order: Order,
    config: SubmitButtonTxtInterface): string {
    let approvalRequired,
      reviewWindow,
      ineligibleMedsAdded,
      syncPlan = false;
    if (this._order.isSyncPlan(order)) {
      syncPlan = true;
    } else if (this._order.isWithinReviewWindow(order)) {
      reviewWindow = true;
      if (config.drugsInBox) {
        const ineligibleMeds = config.drugsInBox.filter(item => !item.eligibility);
        if (ineligibleMeds.length > 0) {
          ineligibleMedsAdded = true;
        }
      }
    } else if (this._order.isApprovalRequired(order)) {
      approvalRequired = true;
    }
    let txt = 'View order details';
    switch (config.buttonType) {
      case 'view':
        txt = reviewWindow
              ? 'View or edit order'
              : (approvalRequired
                 ? 'View and approve order'
                 : 'View order details');
        break;
      default:
        txt = reviewWindow
              ? (config.showReconsentFlow
                 ? (ineligibleMedsAdded
                    ? 'Approve order'
                    : 'Update order')
                 : 'Update order')
              : `Approve ${(config.drugsRemoved || config.drugsAdded)
                           ? 'order with changes'
                           : 'order'}`;
    }
    return txt;
  }
}

/**
 * Generates text for button to approve or submit
 * order and reacts to changes in drugs removed or
 * added to order
 *
 * Can be used anywhere to view order details
 * @Usage
 * // to show text to view order
 *
 * <button appCvsSubmitOrderButton
 * [order]="Order" buttonType="view"></button>
 *
 * // to show text to approve order or submit order with changes
 *
 * <button appCvsSubmitOrderButton
 * [order]="Order"
 * [reviewWindow]="boolean"
 * [bust]="any"
 * [drugsAdded]="number"
 * [drugsRemoved]="number"></button>
 */
@Directive({
  selector: '[appCvsSubmitOrderButton]'
})
export class SubmitOrderButtonDirective extends SubmitButtonTxt implements OnInit,
  OnChanges {
  /**
   * Order object
   */
  @Input() order: Order;
  /**
   * Drugs in Box
   */
  @Input() drugsInBox: any[];
  /**
   * Reconsent switch Akamai
   */
  @Input() showReconsentFlow: boolean;
  /**
   * Flag is used to indicate button is rendered somewhere
   * that leads to order details or edit order
   */
  @Input() buttonType: string;
  /**
   * On edit order view, holds array length of
   * drugs removed from order
   */
  @Input() drugsRemoved: number;
  /**
   * On edit order view, holds array length of
   * drugs added to order
   */
  @Input() drugsAdded: number;
  /**
   * Force update, pass a random value to re-render
   */
  @Input() bust: number;

  constructor(private _elm: ElementRef,
    private _orderDetails: OrderDetailsService) {
    super(_orderDetails);
  }

  /**
   * Angular LifeCycle event
   */
  ngOnInit(): void {
    this.render();
  }

  /**
   * Angular LifeCycle event
   * React to order view changes
   */
  ngOnChanges(changes: SimpleChanges): void {
    this.render();
  }

  /**
   * Render button text as per copy provided
   * and scenario given
   */
  render(): void {
    this._elm.nativeElement.innerText = this.getText(this.order, {
      buttonType: this.buttonType,
      drugsAdded: this.drugsAdded,
      drugsRemoved: this.drugsRemoved,
      drugsInBox: this.drugsInBox,
      showReconsentFlow: this.showReconsentFlow
    });
  }
}

/**
 * Generates text for button to approve or submit
 * order.
 *
 * Can  be used on view order details view or orders tab.
 * Use directive on edit order details
 *
 * @Usage
 * // to show text to view order
 * {{ order | appCvsSubmitOrderTxt:'view'}}
 * // to show text to approve order or submit order
 * {{ order | appCvsSubmitOrderTxt }}
 */
@Pipe({
  name: 'appCvsSubmitOrderTxt'
})
export class SubmitOrderButtonPipe extends SubmitButtonTxt implements PipeTransform {
  constructor(private _orderDetails: OrderDetailsService) {
    super(_orderDetails);
  }

  /**
   *
   */
  transform(order: Order,
    args?: any): any {
    return this.getText(order, { buttonType: 'view' });
  }
}

